import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { removePort, updatePort } from '../../features/equipment/equipmentSlice';
import {
  getElementPorts,
  portParentTypeForElement,
  removeStockerPort,
  updateStockerPort,
} from '../../features/layout/layoutSlice';
import { OBJECT_TYPE_LABELS } from '../../features/equipment/equipmentConstants';
import { CLASS_LABELS } from '../../constants';
import { resolvePorts } from '../../utils/equipmentGeometry';
import {
  BAY_TYPES,
  EQUIPMENT_PORT_KINDS,
  STOCKER_COLUMN_KINDS,
  STOCKER_INVENTORY_LEVELS,
  STOCKER_PORT_KINDS,
  canRemovePort,
  defaultInventoryLevelForColumn,
  labelBayType,
  labelColumnKind,
  labelInventoryLevel,
  labelPortKind,
  portKindForColumnKind,
  resolveBayType,
  resolveColumnKind,
  resolveInventoryLevel,
  resolvePortKind,
  type BayType,
  type Port,
  type PortKind,
  type PortParentType,
  type StockerColumnKind,
  type StockerInventoryLevel,
} from '../../utils/ports';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';
import type { LayoutElement } from '../../types';
import PropertySection from './PropertySection';
import {
  PropertyHint,
  PropertyInput,
  PropertyPanelHeader,
  PropertySelect,
  ReadOnlyRow,
} from './PropertyFields';

type EquipmentProps = {
  kind: 'equipment';
  instance: EquipmentInstance;
  port: Port;
};

type LayoutProps = {
  kind: 'layout';
  element: LayoutElement;
  port: Port;
};

type Props = EquipmentProps | LayoutProps;

function parentTypeForProps(props: Props): PortParentType {
  if (props.kind === 'layout') return portParentTypeForElement(props.element);
  return props.instance.objectType === 'stocker' ? 'stocker' : 'equipment';
}

/**
 * Properties for a selected Load Port: labels, functional kind, bay type and
 * deletion. Bay type is fixed to intrabay on tools, which are never interbay.
 */
const LoadPortProperties: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const elements = useAppSelector((s) => s.layout.elements);
  const templates = useAppSelector((s) => s.equipment.templates);
  const port = props.port;
  const parentType = parentTypeForProps(props);
  const portKind = resolvePortKind(port, parentType);
  const bayType = resolveBayType(port, parentType);
  const columnKind = resolveColumnKind(port, parentType);
  const inventoryLevel = resolveInventoryLevel(port, parentType);
  const canEditBayType = parentType === 'stocker';
  const portKindOptions =
    parentType === 'stocker' ? STOCKER_PORT_KINDS : EQUIPMENT_PORT_KINDS;

  const siblingPorts =
    props.kind === 'layout'
      ? getElementPorts(props.element)
      : resolvePorts(
          props.instance,
          props.instance.templateId ? templates[props.instance.templateId] ?? null : null
        );
  const removable = canRemovePort(siblingPorts);

  const parentName =
    props.kind === 'equipment' ? props.instance.name : props.element.metadata.name;
  const parentTypeLabel =
    props.kind === 'equipment'
      ? OBJECT_TYPE_LABELS[props.instance.objectType] ?? props.instance.objectType
      : CLASS_LABELS[props.element.type] ?? props.element.type;

  const attachedRailId =
    typeof port.metadata?.attachedRailId === 'string'
      ? port.metadata.attachedRailId
      : null;
  const attachedRailName = attachedRailId
    ? elements[attachedRailId]?.metadata.name ?? attachedRailId
    : null;

  const patch = (changes: Partial<Port>) => {
    if (props.kind === 'equipment') {
      dispatch(
        updatePort({
          instanceId: props.instance.id,
          portId: port.id,
          changes,
          skipHistory: true,
        })
      );
      return;
    }
    dispatch(
      updateStockerPort({
        elementId: props.element.id,
        portId: port.id,
        changes,
      })
    );
  };

  const onDelete = () => {
    if (!removable) return;
    if (props.kind === 'equipment') {
      dispatch(removePort({ instanceId: props.instance.id, portId: port.id }));
      return;
    }
    dispatch(removeStockerPort({ elementId: props.element.id, portId: port.id }));
  };

  const edge = port.edge ?? '—';
  const positionLabel = `${edge} · ${(port.rx * 100).toFixed(0)}% × ${(port.ry * 100).toFixed(0)}%`;

  return (
    <div className="p-2.5">
      <PropertyPanelHeader title={port.displayName || port.name} subtitle="Load Port" />
      <div className="space-y-1.5">
        <PropertySection title="General">
          <PropertyInput label="Name *" value={port.name} onChange={(name) => patch({ name })} required />
          <PropertyInput
            label="Display Name *"
            value={String(port.displayName ?? '')}
            onChange={(displayName) => patch({ displayName })}
            placeholder="e.g. Intrabay 1"
            required
            hint="Shown in OHT From / To lists."
          />
          <PropertySelect
              label="Port Type"
              value={portKind}
              onChange={(value) => {
                const next = value as PortKind;
                if (parentType === 'stocker' && (next === 'UNLOAD_RAW' || next === 'UNLOAD_FINISHED')) {
                  const nextColumn = next === 'UNLOAD_FINISHED' ? 'processed' : 'raw';
                  patch({
                    portKind: next,
                    columnKind: nextColumn,
                    inventoryLevel: defaultInventoryLevelForColumn(nextColumn),
                  });
                  return;
                }
                patch({ portKind: next });
              }}
              options={portKindOptions.map((option) => ({ value: option, label: labelPortKind(option) }))}
          />
          {canEditBayType ? (
              <PropertySelect
                label="Bay Type"
                value={bayType}
                onChange={(value) => patch({ bayType: value as BayType })}
                options={BAY_TYPES.map((option) => ({ value: option, label: labelBayType(option) }))}
                hint="Exported as Interbay / Intrabay."
              />
          ) : (
            <ReadOnlyRow label="Bay Type" value={labelBayType(bayType)} />
          )}
          {parentType === 'stocker' && (
            <>
                <PropertySelect
                  label="Column Kind"
                  value={columnKind ?? 'raw'}
                  onChange={(value) => {
                    const next = value as StockerColumnKind;
                    patch({
                      columnKind: next,
                      inventoryLevel: defaultInventoryLevelForColumn(next),
                      portKind: portKindForColumnKind(next),
                    });
                  }}
                  options={STOCKER_COLUMN_KINDS.map((option) => ({ value: option, label: labelColumnKind(option) }))}
                  hint="Raw = inbound; Processed = finished."
                />
                <PropertySelect
                  label="Inventory Level"
                  value={inventoryLevel ?? 'stocker'}
                  onChange={(value) =>
                    patch({ inventoryLevel: value as StockerInventoryLevel })
                  }
                  options={STOCKER_INVENTORY_LEVELS.map((option) => ({ value: option, label: labelInventoryLevel(option) }))}
                  hint="Usually Stocker for Raw, Bay for Processed."
                />
            </>
          )}
        </PropertySection>

        <PropertySection title="Parent">
          <ReadOnlyRow label="Parent Object" value={parentName} />
          <ReadOnlyRow label="Parent Type" value={parentTypeLabel} />
        </PropertySection>

        <PropertySection title="Placement">
          <ReadOnlyRow
            label="Attached Rail"
            value={attachedRailName ?? 'Not attached'}
          />
          <ReadOnlyRow label="Position" value={positionLabel} />
          <PropertyHint>
            Drag the port along the object boundary on the canvas to reposition it.
          </PropertyHint>
        </PropertySection>

        <button
          type="button"
          onClick={onDelete}
          disabled={!removable}
          title={
            removable
              ? 'Delete this load port'
              : 'Each equipment / stocker must keep at least one port'
          }
          className="w-full rounded-md border border-red-200 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent dark:border-red-900 dark:hover:bg-red-950"
        >
          Delete Port
        </button>
      </div>
    </div>
  );
};

export default LoadPortProperties;
